/**
 * Phase M.1 (2026-08-21) — RentalCancelModal.
 *
 * Confirmation modal for cancelling a pre-release rental (any of
 * ``pending_contract`` / ``contract_signed`` / ``coi_pending`` /
 * ``coi_received`` / ``coi_approved``). Fires
 * ``POST /api/admin/rentals/{id}/cancel``.
 *
 * On 200 → toast success, invoke ``onCanceled(freshRental)`` (parent
 * decides whether to refresh or navigate).
 * On 409 already_canceled → toast info + also invoke ``onCanceled(null)``
 * so the parent can refresh (the rental IS already cancelled server-side).
 * On other 4xx → surface the error message inline.
 *
 * Roles: rendered only when the caller can hit the endpoint (checked by
 * the parent — this component doesn't enforce role gating).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const REASON_OPTIONS = [
    { value: "coi_not_provided",     label: "COI / insurance not provided" },
    { value: "customer_request",     label: "Customer requested cancellation" },
    { value: "credit_issue",         label: "Credit / billing issue" },
    { value: "equipment_unavailable", label: "Equipment unavailable" },
    { value: "other",                label: "Other (notes required)" },
];


function CancelPreviewCard({ preview }) {
    // Not yet loaded — 3-bar skeleton.
    if (preview === null) {
        return (
            <div
                className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2"
                data-testid="cancel-preview-skeleton"
            >
                <div className="h-3 rounded bg-slate-200 animate-pulse w-3/4" />
                <div className="h-3 rounded bg-slate-200 animate-pulse w-2/3" />
                <div className="h-3 rounded bg-slate-200 animate-pulse w-1/2" />
            </div>
        );
    }

    // Fetch failed — gray fallback, does NOT block the modal.
    if (preview === false) {
        return (
            <div
                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500"
                data-testid="cancel-preview-unavailable"
            >
                Preview unavailable. The cancel action will still work
                — the server validates state on commit.
            </div>
        );
    }

    // Not-eligible — red banner, submit gets disabled by the parent.
    if (preview.cancel_eligible === false) {
        return (
            <div
                className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                data-testid="cancel-preview-not-eligible"
            >
                This rental cannot be canceled from status{" "}
                <span className="font-mono font-semibold">
                    {preview.rental_status}
                </span>. Use the End-rental flow instead.
            </div>
        );
    }

    // Build the list of true side-effects. Skip null / false sections.
    const lines = [];
    if (preview.contract?.will_be_voided) {
        lines.push({
            testid: "cancel-preview-line-contract",
            text: preview.contract.void_is_hash_chained
                ? "Signed contract will be voided (audit-chained)"
                : "Draft contract will be marked cancelled",
        });
    }
    if (preview.coi_request?.will_be_canceled) {
        lines.push({
            testid: "cancel-preview-line-coi",
            text: "COI request will be cancelled",
        });
    }
    if (preview.equipment?.will_be_released) {
        lines.push({
            testid: "cancel-preview-line-equipment",
            text: `Unit ${preview.equipment.unit_number} will be released`,
        });
    }
    if (preview.notifications?.will_be_sent) {
        const to = preview.notifications.customer_email
            || `${preview.notifications.admin_recipients?.length || 0} admin(s)`;
        lines.push({
            testid: "cancel-preview-line-notification",
            text: `Email notification will be sent to ${to}`,
        });
    }

    return (
        <div
            className="rounded-md border border-slate-200 bg-slate-50 p-3"
            data-testid="cancel-preview-card"
        >
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                What will happen
            </div>
            {lines.length === 0 ? (
                <div className="text-xs text-slate-500">
                    Rental status flip only — no linked artifacts.
                </div>
            ) : (
                <ul className="space-y-1.5">
                    {lines.map((l) => (
                        <li
                            key={l.testid}
                            className="flex items-start gap-2 text-sm text-slate-700"
                            data-testid={l.testid}
                        >
                            <CheckCircle2
                                size={14}
                                className="mt-0.5 flex-none text-slate-500"
                            />
                            <span>{l.text}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}


export default function RentalCancelModal({
    open,
    onOpenChange,
    rental,
    onCanceled,
}) {
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [errorText, setErrorText] = useState("");
    // Phase M.1.1 — preview card state. ``null`` = not-yet-loaded (skeleton).
    // ``false`` = fetch failed (renders "Preview unavailable" fallback).
    // ``object`` = loaded payload from GET /cancel-preview.
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!open || !rental?.id) return;
        let cancelled = false;
        setPreview(null);
        (async () => {
            try {
                const { data } = await api.get(
                    `/admin/rentals/${rental.id}/cancel-preview`,
                );
                if (!cancelled) setPreview(data);
            } catch {
                if (!cancelled) setPreview(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, rental?.id]);

    const notesRequired = reason === "other";
    const canSubmit = useMemo(() => {
        if (!reason) return false;
        if (notesRequired && !notes.trim()) return false;
        if (busy) return false;
        // Phase M.1.1 — hard block when the preview said "not eligible".
        if (preview && typeof preview === "object" && preview.cancel_eligible === false) {
            return false;
        }
        return true;
    }, [reason, notes, notesRequired, busy, preview]);

    async function onSubmit() {
        if (!canSubmit || !rental) return;
        setBusy(true);
        setErrorText("");
        try {
            const { data } = await api.post(
                `/admin/rentals/${rental.id}/cancel`,
                { reason, notes: notes.trim() || null },
            );
            toast.success("Rental canceled");
            onCanceled?.(data);
            onOpenChange?.(false);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409 && detail?.code === "already_canceled") {
                toast.info("This rental was already canceled.");
                onCanceled?.(null);
                onOpenChange?.(false);
                return;
            }
            setErrorText(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    function handleOpenChange(next) {
        if (!next) {
            // Reset transient state on close so the next open is clean.
            setReason("");
            setNotes("");
            setErrorText("");
        }
        onOpenChange?.(next);
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                className="max-w-lg"
                data-testid="rental-cancel-modal"
            >
                <DialogHeader>
                    <DialogTitle>Cancel this rental?</DialogTitle>
                    <DialogDescription>
                        This will void the signed contract (if any),
                        release the equipment claim, and cancel any
                        outstanding COI request. Customer + ops will be
                        notified.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div
                        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex gap-2"
                        data-testid="rental-cancel-warning"
                    >
                        <AlertTriangle size={16} className="mt-0.5 flex-none" />
                        <div>
                            This action cannot be undone. If a signed
                            contract exists it will be voided in the
                            audit trail. Terminal rentals and active
                            rentals can not be canceled here — use the
                            End-rental flow instead.
                        </div>
                    </div>

                    {/* Phase M.1.1 (2026-08-21) — "What will happen" preview
                        card. Neutral, not warning-colored — this is
                        informational context, not a second warning. */}
                    <CancelPreviewCard preview={preview} />

                    <div className="space-y-1.5">
                        <Label htmlFor="rental-cancel-reason">
                            Reason
                        </Label>
                        <select
                            id="rental-cancel-reason"
                            data-testid="rental-cancel-reason-select"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                            disabled={busy}
                        >
                            <option value="">— Pick a reason —</option>
                            {REASON_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-1.5">
                        <Label htmlFor="rental-cancel-notes">
                            Notes {notesRequired && (
                                <span className="text-red-600">*</span>
                            )}
                        </Label>
                        <Textarea
                            id="rental-cancel-notes"
                            data-testid="rental-cancel-notes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder={notesRequired
                                ? "Explain the cancellation (visible to ops)"
                                : "Optional — captured in the audit trail"
                            }
                            rows={3}
                            disabled={busy}
                        />
                        {notesRequired && !notes.trim() && (
                            <p className="text-xs text-red-600">
                                Notes are required when reason is &quot;Other&quot;.
                            </p>
                        )}
                    </div>

                    {errorText && (
                        <div
                            className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                            data-testid="rental-cancel-error"
                        >
                            {errorText}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={busy}
                        data-testid="rental-cancel-keep-btn"
                    >
                        Keep Rental
                    </Button>
                    <Button
                        onClick={onSubmit}
                        disabled={!canSubmit}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="rental-cancel-confirm-btn"
                    >
                        {busy
                            ? "Canceling…"
                            : "Cancel Rental & Void Contract"
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
